import { toast } from "@/components/Toaster/Toaster";
import { isApiErrorResponse } from "@/server/openapi/errors";
import {
  STYTCH_EMAIL_NOT_EXPLICITLY_ALLOWED_ERROR_CODE,
  STYTCH_EMAIL_NOT_IN_ALLOWLIST_ERROR_CODE,
  STYTCH_INVALID_DOMAIN_ERROR_CODE,
  STYTCH_ORGANIZATION_SLUG_ALREADY_USED_ERROR_CODE,
  STYTCH_DUPLICATE_MEMBER_EMAIL_ERROR_CODE,
  VERCEL_INVALID_DOMAIN_ERROR_CODE,
  VERCEL_FORBIDDEN_ERROR_CODE,
  VERCEL_NOT_FOUND_ERROR_CODE,
  VERCEL_DOMAIN_TAKEN_ERROR_CODE,
  EXTERNAL_TIMEOUT_ERROR_CODE,
  INTERFACES_DOMAIN_IN_USE_ERROR_CODE,
  TABLES_PERMISSIONS_ERRORS_ERROR_CODE,
  TABLES_LIMITS_TOO_MANY_FIELDS_ERROR_CODE,
  TABLES_LIMITS_TOO_MANY_TABLES_ERROR_CODE,
  TABLES_LIMITS_TOO_MANY_RECORDS_ERROR_CODE,
  INTERFACES_LIMITS_PAGES_ERROR_CODE,
  INTERFACES_LIMITS_BLOCKS_PER_PAGE_ERROR_CODE,
  BLOCK_CHATBOT_DIRECTIVE_TOO_LONG_ERROR_CODE,
  INTERFACES_SLUG_IN_USE_ERROR_CODE,
  INTERFACES_PAGE_SLUG_IN_USE_ERROR_CODE,
  TABLES_MISSING_ACCESS_TOKEN_ERROR_CODE,
  AUTH_INVALID_GRANT_ERROR_CODE,
  AUTH_INVALID_TOKEN_ERROR_CODE,
  AUTH_PROJECT_SESSION_EXPIRED_ERROR_CODE,
  AUTH_NOT_LOGGED_IN_ERROR_CODE,
  AUTH_INVALID_REFRESH_TOKEN_ERROR_CODE,
  APP_VALIDATION_ERROR_CODE,
  UNKNOWN_ERROR_CODE,
  INTERFACES_PERMISSIONS_ERROR_CODE,
  INTERFACES_COLLABORATOR_ALREADY_EXISTS_ERROR_CODE,
  INTERFACES_COLLABORATOR_CANNOT_BE_CREATOR_ERROR_CODE,
  INTERFACES_COLLABORATOR_ACCOUNT_MEMBER_NOT_FOUND_ERROR_CODE,
  FORBIDDEN_ERROR_CODE,
  INTERFACES_COLLABORATORS_CANNOT_DELETE_CREATOR_ERROR_CODE,
} from "@/lib/errorCodes";
import { TRPCClientError } from "@trpc/client";
import { get, isString } from "lodash";

export function getFriendlyErrorMessage(clientError: unknown): string {
  const errorCode = extractCodeFromError(clientError);

  switch (errorCode) {
    case FORBIDDEN_ERROR_CODE:
      return "You are not authorized to perform this action.";

    case STYTCH_EMAIL_NOT_IN_ALLOWLIST_ERROR_CODE:
    case STYTCH_EMAIL_NOT_EXPLICITLY_ALLOWED_ERROR_CODE:
      return "Email has not been added to this interface yet. Please contact your administrator.";
    case STYTCH_INVALID_DOMAIN_ERROR_CODE:
      return "Invalid domain.";
    case STYTCH_ORGANIZATION_SLUG_ALREADY_USED_ERROR_CODE:
      /**
       * Since the fact that we create a Stytch organization is hidden from the user,
       * the message here is vague.
       */
      return "An error occurred. Please try again.";
    case STYTCH_DUPLICATE_MEMBER_EMAIL_ERROR_CODE:
      return "This email is already in use.";

    case VERCEL_INVALID_DOMAIN_ERROR_CODE:
      return "Domain is invalid.";
    case VERCEL_DOMAIN_TAKEN_ERROR_CODE:
      return "Domain is already in use.";
    case VERCEL_NOT_FOUND_ERROR_CODE:
      return "Not found.";
    case VERCEL_FORBIDDEN_ERROR_CODE:
      return "You are not authorized to perform this action.";

    case EXTERNAL_TIMEOUT_ERROR_CODE:
      return "Third-party service took too long to respond.";

    case INTERFACES_DOMAIN_IN_USE_ERROR_CODE:
      return "Custom domain is already claimed by another Interface.";
    case INTERFACES_LIMITS_PAGES_ERROR_CODE:
      return "You have reached the page limit for your plan. Please upgrade your plan to create more pages or Interfaces.";
    case INTERFACES_LIMITS_BLOCKS_PER_PAGE_ERROR_CODE:
      return "You have reached the maximum number of components that can be added to a page. Please consider creating a new page.";
    case INTERFACES_SLUG_IN_USE_ERROR_CODE:
      return "Subdomain is already in use.";
    case INTERFACES_PAGE_SLUG_IN_USE_ERROR_CODE:
      return "Page URL is already in use.";

    case TABLES_LIMITS_TOO_MANY_TABLES_ERROR_CODE:
      return "You have reached the maximum number of tables allowed in your account. Please upgrade your account to add more tables.";
    case TABLES_LIMITS_TOO_MANY_FIELDS_ERROR_CODE:
      return "You have reached the maximum number of fields allowed in your account. Please upgrade your account to add more fields.";
    case TABLES_LIMITS_TOO_MANY_RECORDS_ERROR_CODE:
      return "You have reached the maximum number of records allowed in your account. Please upgrade your account to add more records.";
    case TABLES_PERMISSIONS_ERRORS_ERROR_CODE:
      return "This page is connected to a Table you don't have access to.";
    case TABLES_MISSING_ACCESS_TOKEN_ERROR_CODE:
      return "An unexpected error occurred. Please try again.";

    case BLOCK_CHATBOT_DIRECTIVE_TOO_LONG_ERROR_CODE:
      return "Chatbot directive is too long.";

    case AUTH_NOT_LOGGED_IN_ERROR_CODE:
      return "You are not logged in.";
    case AUTH_PROJECT_SESSION_EXPIRED_ERROR_CODE:
      return "Your session has expired. Please log in again.";
    case AUTH_INVALID_TOKEN_ERROR_CODE:
      return "Your token is invalid.";
    case AUTH_INVALID_GRANT_ERROR_CODE:
      return "Your grant is invalid.";
    case AUTH_INVALID_REFRESH_TOKEN_ERROR_CODE:
      return "Your refresh token is invalid.";

    case APP_VALIDATION_ERROR_CODE:
      return "Validation failed. Please try again.";

    case INTERFACES_PERMISSIONS_ERROR_CODE:
      return "You are not authorized to perform this action.";
    case INTERFACES_COLLABORATOR_ALREADY_EXISTS_ERROR_CODE:
      return "This editor already exists in the Interface.";
    case INTERFACES_COLLABORATOR_CANNOT_BE_CREATOR_ERROR_CODE:
      return "The Interface creator cannot be an editor.";
    case INTERFACES_COLLABORATOR_ACCOUNT_MEMBER_NOT_FOUND_ERROR_CODE:
      return "The editor you are trying to add does not exist in the Zapier account.";

    case INTERFACES_COLLABORATORS_CANNOT_DELETE_CREATOR_ERROR_CODE:
      return "Interface owner cannot be removed";

    case UNKNOWN_ERROR_CODE:
      return "An unexpected error occurred. Please try again.";

    default: {
      return "Something went wrong. Please try again.";
    }
  }
}

export function getFriendlyErrorMessageWithErrorId(
  clientError: unknown
): string {
  let message: string;
  message = getFriendlyErrorMessage(clientError);

  const formattedErrorId = extractErrorId(clientError);
  if (formattedErrorId) message += `\n${formattedErrorId}`;

  return message;
}

export function toastFriendlyErrorMessage(
  clientError: unknown,
  errorMessageOverride?: string
) {
  let baseMessage: string, msgWithErrorCode: string;

  if (errorMessageOverride) {
    baseMessage = errorMessageOverride;
    msgWithErrorCode = errorMessageOverride;
  } else {
    baseMessage = getFriendlyErrorMessage(clientError);
    msgWithErrorCode = getFriendlyErrorMessageWithErrorId(clientError);
  }

  return toast.error(
    {
      message: msgWithErrorCode,
    },
    {
      id: baseMessage,
      duration: Infinity,
    }
  );
}

export function extractErrorId(clientError: unknown): string | undefined {
  const errorId = get(clientError, ["data", "id"]) as string | undefined;

  if (errorId) {
    return `\`«${errorId}»\``;
  }
}

function extractCodeFromError(clientError: unknown): string | undefined {
  if (isApiErrorResponse(clientError)) {
    return clientError.code;
  }

  if (clientError instanceof TRPCClientError) {
    const code = isString(clientError.data?.code)
      ? String(get(clientError.data, "code"))
      : "unknown";

    return code;
  }

  return "unknown";
}

/**
 * We probably want to find a better place for this function.
 * For now, we decided to defer refactoring as we are in the process of migrating from TRPC to OpenAPI,
 * and that forces us to have two error.ts files, which makes it hard to put this function in the right place.
 */
export function isTablesPermissionsClientError(clientError: unknown): boolean {
  return (
    extractCodeFromError(clientError) === TABLES_PERMISSIONS_ERRORS_ERROR_CODE
  );
}
